import {
  HrmRequestFormResponse,
  ITRequestFormResponse,
  MaterialRequisitionFormResponse,
  ResignationRequestFormResponse,
} from '@Types/api/form';
import { RequestType } from '@Enum/index';
import useStatusFinderByNum from '@Hooks/useStatusFinderByNum';
import { RequestTypes } from '@Types/index';
import { parseDateTime } from '@Utils/transformDateTime';
import {
  useAppSelector,
  useFindRequestType,
  useLeaveTypeTitleFinderById,
} from '@Hooks/index';
import { getResumptionWorkShiftDict } from '@Store/selectors/dictionarySelector';
import {
  ApprovalsTasksRequisitionResponse,
  ApprovalsTasksResponse,
} from '@Types/api/approvalsList';
import { AttendanceProfileResponse } from '@Types/api/profile/AttendanceProfileList';

interface MapperValue {
  requestType: string;
  status: string;
  items: Array<{ label: string; value: string }>;
}

const mapper: MapperValue = {
  requestType: '',
  status: '',
  items: [
    { label: 'From', value: '' },
    { label: 'To', value: '' },
    { label: 'Total', value: '' },
  ],
};

const useSmartMapper = (
  type: RequestTypes
): {
  transformItem: (
    item:
      | HrmRequestFormResponse
      | ITRequestFormResponse
      | ResignationRequestFormResponse
      | MaterialRequisitionFormResponse
      | ApprovalsTasksResponse
      | ApprovalsTasksRequisitionResponse
      | AttendanceProfileResponse
  ) => MapperValue;
} => {
  const { findStatus } = useStatusFinderByNum(type);
  const { findRequestTitle } = useLeaveTypeTitleFinderById(type);
  const resumptionWorkShifts = useAppSelector(getResumptionWorkShiftDict);
  const { findRequestTypeBy } = useFindRequestType();

  const transformItem = (
    item:
      | HrmRequestFormResponse
      | ITRequestFormResponse
      | ResignationRequestFormResponse
      | MaterialRequisitionFormResponse
      | ApprovalsTasksResponse
      | ApprovalsTasksRequisitionResponse
      | AttendanceProfileResponse
  ): MapperValue => {
    const {
      leaveTypeID,
      reqStatusId,
      startDate,
      endDate,
      totDays,
      ticketRequired,
      travelDate,
      returnDate,
      updatedDate,
      destination,
    } = item as HrmRequestFormResponse;

    const salaryLanguage = destination === 'E' ? 'English' : 'Arabic';

    const { reqItemId, empNumber, createdDate, empName, statusId } =
      item as ITRequestFormResponse;

    const {
      reason,
      empNumber: resignationEmpNumber,
      resDate,
      lastWrkngDate,
      statusId: resignationStatusId,
    } = item as ResignationRequestFormResponse;

    const {
      reqNo,
      statusId: materialStatus,
      reqDate,
      createdOn,
    } = item as MaterialRequisitionFormResponse;

    const { employeeName, created, requestTypeId } =
      item as ApprovalsTasksResponse;

    const { created: MRCreated, employeeName: MREmployeeName } =
      item as ApprovalsTasksRequisitionResponse;

    const { AttDate, InTime, OutTime } = item as AttendanceProfileResponse;

    switch (type) {
      case RequestType.LEAVE:
      case RequestType.PASSPORT:
      case RequestType.ATTENDANCE:
        return {
          requestType: findRequestTitle(leaveTypeID),
          status: findStatus(reqStatusId)?.codeDesc || '',
          items: [
            { label: 'From', value: parseDateTime(startDate) },
            {
              label: 'To',
              value: parseDateTime(endDate),
            },
            { label: 'Total', value: String(totDays || '') },
          ],
        };
      case RequestType.ATTENDANCE_PROFILE:
        return {
          requestType: 'Attendance',
          status: '',
          items: [
            { label: 'In Time', value: InTime },
            {
              label: 'Date',
              value: AttDate,
            },
            { label: 'Out Time', value: OutTime },
          ],
        };

      case RequestType.RESUMPTION:
        return {
          requestType: findRequestTitle(leaveTypeID),
          status: findStatus(reqStatusId)?.codeDesc || '',
          items: [
            { label: 'From', value: parseDateTime(startDate) },
            {
              label: 'To',
              value: parseDateTime(endDate),
            },
            {
              label: 'Work Shift',
              value:
                resumptionWorkShifts.find(
                  (workShift) => workShift.codeNum === ticketRequired
                )?.codeDesc || '',
            },
          ],
        };
      case RequestType.SALARY:
        return {
          requestType: findRequestTitle(leaveTypeID),
          status: findStatus(reqStatusId)?.codeDesc || '',
          items: [
            { label: 'Required by', value: parseDateTime(startDate) },
            {
              label: 'Language',
              value: salaryLanguage,
            },
            { label: 'Date', value: parseDateTime(updatedDate) },
          ],
        };
      case RequestType.OVERSEAS:
        return {
          requestType: findRequestTitle(leaveTypeID),
          status: findStatus(reqStatusId)?.codeDesc || '',
          items: [
            { label: 'From', value: parseDateTime(travelDate) },
            {
              label: 'To',
              value: parseDateTime(returnDate),
            },
            {
              label: 'Total',
              value: String(totDays || ''),
            },
          ],
        };
      case RequestType.IT_REQUISITION:
        return {
          requestType: findRequestTitle(reqItemId),
          status: findStatus(statusId)?.codeDesc || '',
          items: [
            { label: 'To', value: empName || '' },
            {
              label: 'Employee ID',
              value: empNumber,
            },
            {
              label: 'Join Date',
              value: parseDateTime(createdDate),
            },
          ],
        };
      case RequestType.RESIGNATION:
        return {
          requestType: findRequestTitle(reason),
          status: findStatus(resignationStatusId)?.codeDesc || '',
          items: [
            { label: 'Employee ID', value: resignationEmpNumber },
            {
              label: 'Resignation Date',
              value: parseDateTime(resDate),
            },
            {
              label: 'Last Day',
              value: parseDateTime(lastWrkngDate),
            },
          ],
        };
      case RequestType.MATERIAL:
        return {
          requestType: reqNo,
          status: findStatus(materialStatus)?.codeDesc || '',
          items: [
            { label: 'Request Date', value: parseDateTime(reqDate) },
            {
              label: 'Created On',
              value: parseDateTime(createdOn),
            },
            {
              label: 'Request Type',
              value: 'Material',
            },
          ],
        };

      case RequestType.ESS_TASK_REQUEST:
        return {
          requestType: findRequestTypeBy(requestTypeId, 'codeNum')?.codeDesc,
          status: 'Pending',
          items: [
            { label: 'From', value: employeeName },
            {
              label: 'Date',
              value: parseDateTime(created, 'DD/MM/YYYY'),
            },
            { label: 'Time', value: parseDateTime(created, 'HH:mm') },
          ],
        };
      case RequestType.MR_TASK_REQUEST:
        return {
          requestType: 'Material',
          status: 'Pending',
          items: [
            { label: 'From', value: MREmployeeName },
            {
              label: 'Date',
              value: parseDateTime(MRCreated, 'DD/MM/YYYY'),
            },
            { label: 'Time', value: parseDateTime(MRCreated, 'HH:mm') },
          ],
        };

      default:
        return mapper;
    }
  };

  return { transformItem };
};

export default useSmartMapper;
